import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { ROUTES } from '../../utils/routes';
import { useNavigate } from 'react-router-dom';
import { usePushEvents } from '../../hooks/PushEvents';
import { FeatureFlag } from '../../constants';
import { useFeatureFlag } from '../../providers';
import { getLoginTypeLocalStorage } from '../../providers/DonorProvider/donorLocalStorage';
import {
    lastLoginBannerDismiss,
    lastLoginBannerShown,
} from '../../hooks/PushEvents/clevertapEvents';
import { DonorWithWallet } from '../../providers/DonorProvider/interfaces';
import { useDonorProfile } from '../../api';

export const LastLoginContext = createContext<{
    show: boolean;
    forceHide: boolean;
    type: 'password' | 'facebook' | undefined;
    donor: DonorWithWallet | undefined;
    donorName: string | undefined;
    donorEmail: string | undefined;
    handleLogin: () => unknown;
    handleCancel: () => unknown;
    handleFacebookClose: () => unknown;
    handleLoginWithPassword: () => unknown;
    setForceHide: React.Dispatch<React.SetStateAction<boolean>>;
}>({
    show: false,
    type: undefined,
    donor: undefined,
    forceHide: false,
    donorName: undefined,
    donorEmail: undefined,
    setForceHide: () => null,
    handleCancel: () => null,
    handleLogin: () => null,
    handleLoginWithPassword: () => null,
    handleFacebookClose: () => null,
});

export const useLastLogin = () => useContext(LastLoginContext);

let timeout: number = 0;
const LastLoginProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const push = useNavigate();
    const [show, setShow] = useState<boolean>(false);
    const [forceHide, setForceHide] = useState<boolean>(false);
    const { isFeatureEnabled } = useFeatureFlag();
    const { pushEvent } = usePushEvents();
    const { donorProfile: donor, hasNeverFetched, isError } = useDonorProfile();
    const loginType = getLoginTypeLocalStorage();
    const type = loginType?.loginType;
    const donorName = loginType?.donorName;
    const donorEmail = loginType?.donorEmail;

    useEffect(() => {
        window.clearTimeout(timeout);
        const shouldShow =
            Boolean(type) &&
            isFeatureEnabled(FeatureFlag.GIVING_REMEMBERLASTLOGIN_OGA);
        if (hasNeverFetched) {
            timeout = window.setTimeout(() => {
                setShow(shouldShow);
                shouldShow && pushEvent(lastLoginBannerShown);
            }, 600);
        } else if (isError) {
            setShow(shouldShow);
            shouldShow && pushEvent(lastLoginBannerShown);
        } else if (donor) {
            setShow(false);
        }
        // while the profile request is in flight, leave the banner as it is
    }, [hasNeverFetched, isError, donor, pushEvent, isFeatureEnabled, type]);

    const handleFacebookClose = useCallback(() => {
        setShow(false);
        pushEvent(lastLoginBannerDismiss, {
            reason: 'Facebook Button Clicked',
        });
    }, [setShow, pushEvent]);

    const handleCancel = useCallback(() => {
        pushEvent(lastLoginBannerDismiss, {
            reason: 'Cancel button clicked',
        });
    }, [pushEvent]);

    const handleLogin = useCallback(() => {
        setShow(false);
        pushEvent(lastLoginBannerDismiss, {
            reason: 'Sign in with another account button clicked',
        });
        push(ROUTES.login);
    }, [push, pushEvent]);
    const handleLoginWithPassword = useCallback(() => {
        setShow(false);
        pushEvent(lastLoginBannerDismiss, {
            reason: 'Sign in with password button clicked',
        });
        push(ROUTES.emailLogin(donorEmail));
    }, [pushEvent, push, donorEmail]);

    return (
        <LastLoginContext.Provider
            value={{
                show,
                type,
                donor,
                donorName,
                donorEmail,
                forceHide,
                setForceHide,
                handleCancel,
                handleLogin,
                handleLoginWithPassword,
                handleFacebookClose,
            }}
        >
            {children}
        </LastLoginContext.Provider>
    );
};

export default React.memo(LastLoginProvider);
